import { Fragment } from 'react';
import { TFunction } from 'i18next';
import {
  Documentation,
  DocumentationValidationStatusEnum,
  FormTemplateQuestionFieldOption,
} from 'graphql-common';
import DocumentationPreview from '@lib/components/DocumentationPreview/DocumentationPreview';
import { DocumentationPreviewEnum } from '@lib/components/DocumentationPreview/enums';
import IconButtonWithDropDown from '@lib/components/IconButtonWithDropDown/IconButtonWithDropDown';
import getDocumentActionsMenu from '@lib/components/LinkedDocuments/utils/getDocumentActionsMenu';
import ControllerErrorRender from '@lib/components/ReactHookForm/ControllerErrorRender';
import SupportiveMessage, {
  SupportiveMessageColor,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import isDocOptionSatisfied from 'routes/Task/utils/isDocOptionSatisfied';
import { DocumentReadRecord } from './types';

type Props = {
  option: FormTemplateQuestionFieldOption;
  doc: Documentation | undefined;
  readRecords: Record<string, DocumentReadRecord>;
  isSubmitted: boolean;
  disabled?: boolean;
  onViewDocument: (doc: Documentation) => void;
  onVersionNotes: (doc: Documentation) => void;
  onToggleFavorite: (doc: Documentation) => void;
  onDetails: (doc: Documentation) => void;
  t: TFunction<'translation', undefined>;
};

export default function DocumentQuestionOption(props: Props) {
  const {
    option,
    doc,
    readRecords,
    isSubmitted,
    disabled,
    onViewDocument,
    onVersionNotes,
    onToggleFavorite,
    onDetails,
    t,
  } = props;

  const docId = option.optionDocumentation?.documentationId;
  if (!docId) return null;

  // `isRead` folds in BE-derived `doc.read` so reload + revisit doesn't
  // re-show the Unread chip / inline error for already-acknowledged docs.
  const isRead = isDocOptionSatisfied(docId, doc, readRecords);
  const isRequiredToRead = !!option.optionDocumentation?.requiredToRead;
  const showReadError = !disabled && isRequiredToRead && !isRead && isSubmitted;

  const isUnavailable =
    !doc ||
    doc.validationStatus !== DocumentationValidationStatusEnum.Validated;

  if (isUnavailable) {
    return (
      <SupportiveMessage
        text={t('document-unavailable')}
        leftIconSymbolsOutlined
        color={SupportiveMessageColor.Gray}
        hasBottomSpace={false}
      />
    );
  }

  const openDoc = () => onViewDocument(doc as Documentation);
  const actionsMenu = getDocumentActionsMenu({
    item: doc as Documentation,
    onDetails,
    onToggleFavorite,
    onVersionNotes,
    onViewDocument,
    hasDetailsUrl: true,
    t,
  });

  return (
    <>
      <DocumentationPreview
        attachment={doc.currentVersion?.attachmentAttached}
        view={DocumentationPreviewEnum.DocumentRowCard}
        customName={doc.name}
        onClick={openDoc}
        isFavorite={!!doc.favorited}
        isUnread={!disabled && !isRead}
        notes={doc.currentVersion?.notes || undefined}
        actions={
          <IconButtonWithDropDown
            buttonProps={{
              icon: 'more_vert',
            }}
            popoverProps={{
              width: 220,
              placement: 'bottomRight',
            }}
            navItems={actionsMenu}
          />
        }
      />
      {showReadError && (
        <ControllerErrorRender
          error={{
            type: 'required',
            message: t('please-read-the-document'),
          }}
        />
      )}
    </>
  );
}
